<script setup lang="ts">
import { ref, watch, reactive, onMounted } from 'vue'
import { vMaska, type MaskInputOptions, type MaskaDetail } from 'maska'

defineOptions({ inheritAttrs: false })
const props = defineProps<{
  error?: string | null
  mask?: MaskInputOptions
  wrapperClass?: string
  innerClass?: string
  modelValue: string | number
  markDirty?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [string]
}>()

let lastValue: string | number = props.modelValue
const localValue = ref(props.modelValue)
watch(
  () => props.modelValue,
  (value) => {
    if (value !== lastValue) {
      localValue.value = value
    }
  }
)

const isDirty = ref(props.markDirty)
const input = ref<HTMLInputElement | null>(null)
const normalizedValue = reactive<MaskaDetail>({
  masked: '',
  unmasked: '',
  completed: true
})

const normalizeInput = (e: Event) => {
  const target = e.target! as HTMLInputElement
  const value = target.value
  lastValue = props.mask ? normalizedValue.unmasked : value
  emit('update:modelValue', lastValue)
}

onMounted(() => {
  input.value?.closest('form')?.addEventListener('submit', () => {
    isDirty.value = true
  })
})
</script>

<template>
  <div class="input__outer" :class="wrapperClass">
    <div class="input__wrapper" :class="innerClass">
      <slot name="icon" />
      <input
        ref="input"
        v-maska:[mask]="normalizedValue"
        class="input__field"
        v-bind="$attrs"
        :value="modelValue"
        @blur="isDirty = true"
        @input="normalizeInput"
      />
    </div>
    <div v-if="error && isDirty" class="input__error">
      {{ error }}
    </div>
  </div>
</template>

<style lang="scss">
.input {
  &__wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    align-self: stretch;
    line-height: 24px;

    border-radius: 8px;
    background: #2c2d3e;
    padding: 10px 14px;
    box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);

    &:hover {
      outline: 1px solid #86899e;
      cursor: pointer;
    }

    &:has(input:focus) {
      outline: 1px solid #3482f5;
    }
  }

  &__field {
    flex: 1;
    background-color: inherit;
    outline: none;
    border: none;
    color: #fff;
    width: 100%;

    &::placeholder {
      color: #86899e;
    }
  }

  &__error {
    color: #f83737;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.2;
    margin-top: 0.2rem;
    margin-bottom: 0.2rem;

    &--hidden {
      visibility: hidden;
    }
  }
}
</style>
